import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Fade,
  Chip,
  Stack,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
  EventBusy as ExpiredIcon,
  Block as InactiveIcon,
  Autorenew as ReactivatedIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

export default function UserHomepage() {
  const navigate = useNavigate();
  const [importados, setImportados] = useState([]);
  const [formulariosGenerales, setFormulariosGenerales] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filtro, setFiltro] = useState('Abiertos');
  const [tipoPersona, setTipoPersona] = useState('juridica');
  const [loadingId, setLoadingId] = useState(null);

  const intervalRef = useRef(null);

  const formatFechaDisplay = (fecha) => {
    if (!fecha) return 'Sin límite';

    if (typeof fecha === 'string' && fecha.includes('-')) {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year}`;
    }

    if (fecha instanceof Date) {
      const day = fecha.getDate().toString().padStart(2, '0');
      const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
      const year = fecha.getFullYear();
      return `${day}/${month}/${year}`;
    }

    return 'Sin límite';
  };

  const formatFechaHoraDisplay = (fecha) => {
    if (!fecha) return '—';

    if (typeof fecha === 'string' && fecha.includes('-')) {
      const d = new Date(fecha);
      if (isNaN(d)) return fecha;
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const seconds = d.getSeconds().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    if (fecha instanceof Date && !isNaN(fecha)) {
      const day = fecha.getDate().toString().padStart(2, '0');
      const month = (fecha.getMonth() + 1).toString().padStart(2, '0');
      const year = fecha.getFullYear();
      const hours = fecha.getHours().toString().padStart(2, '0');
      const minutes = fecha.getMinutes().toString().padStart(2, '0');
      const seconds = fecha.getSeconds().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    return '—';
  };

  const obtenerUsuario = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/usuarios/me`, { credentials: 'include' });
      if (!res.ok) throw new Error('Sin sesión');
      const user = await res.json();
      setTipoPersona(user.tipo_persona || 'juridica');
      localStorage.setItem('userId', user.id_usuario);
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  const mostrarSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const obtenerFormulariosImportados = useCallback(async () => {
    try {
      const userId = localStorage.getItem('userId');
      const res = await fetch(`${API_URL}/api/formularios/usuario/${userId}/realtime`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      if (!res.ok) throw new Error('Error al cargar');
      const data = await res.json();

      const formulariosVisibles = data.filter(form => form.visible === true);

      setImportados(formulariosVisibles);
    } catch (error) {
      console.error('Error obteniendo formularios:', error);
    }
  }, []);

  const obtenerFormulariosGenerales = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/formularios-generales/list`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      if (!res.ok) throw new Error('Error al cargar formularios generales');
      const data = await res.json();

      const formulariosMap = data.reduce((acc, fg) => {
        acc[fg.id_formulario_general] = fg;
        return acc;
      }, {});

      setFormulariosGenerales(formulariosMap);
    } catch (error) {
      console.error('Error obteniendo formularios generales:', error);
    }
  }, []);

  const obtenerTodosLosDatos = useCallback(async () => {
    try {
      const userId = localStorage.getItem('userId');

      const res = await fetch(`${API_URL}/api/formularios/usuario/${userId}/realtime`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });

      if (res.ok) {
        const data = await res.json();
        const formulariosVisibles = data.filter(form => form.visible === true);
        setImportados(formulariosVisibles);
      }

      const resGenerales = await fetch(`${API_URL}/api/formularios-generales/list`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });

      if (resGenerales.ok) {
        const dataGenerales = await resGenerales.json();
        const formulariosMap = dataGenerales.reduce((acc, fg) => {
          acc[fg.id_formulario_general] = fg;
          return acc;
        }, {});
        setFormulariosGenerales(formulariosMap);
      }

    } catch (error) {
      console.error('Error en actualización automática:', error);
    }
  }, []);

  const fechaVencida = useCallback((fecha) => {
    if (!fecha) return false;

    const fechaVencimiento = new Date(fecha);
    const hoy = new Date();

    const vencimientoLocal = new Date(fechaVencimiento.getTime() + fechaVencimiento.getTimezoneOffset() * 60000);
    vencimientoLocal.setHours(23, 59, 59, 999);

    return vencimientoLocal < hoy;
  }, []);

  const fechaInicioFutura = useCallback((fechaInicio) => {
    if (!fechaInicio) return false;

    const fechaInicioDate = new Date(fechaInicio);
    const hoy = new Date();

    const inicioLocal = new Date(fechaInicioDate.getTime() + fechaInicioDate.getTimezoneOffset() * 60000);
    inicioLocal.setHours(0, 0, 0, 0);

    const hoyLocal = new Date(hoy.getTime() + hoy.getTimezoneOffset() * 60000);
    hoyLocal.setHours(0, 0, 0, 0);

    return inicioLocal > hoyLocal;
  }, []);

  const esReactivado = useCallback((formulario) => {
    return formulario.fecha_reactivacion && formulario.fecha_reactivacion !== null;
  }, []);

  const formularioGeneralInactivo = useCallback((formularioIndividual) => {
    if (!formularioIndividual.id_formulario_general) return false;
    const formularioGeneral = formulariosGenerales[formularioIndividual.id_formulario_general];
    return formularioGeneral && formularioGeneral.estado === 'inactivo';
  }, [formulariosGenerales]);

  const obtenerDatosFormularioGeneral = useCallback((formularioIndividual) => {
    if (!formularioIndividual.id_formulario_general) {
      return { titulo: 'Formulario sin título', descripcion: '' };
    }
    const formularioGeneral = formulariosGenerales[formularioIndividual.id_formulario_general];
    if (formularioGeneral) {
      return {
        titulo: formularioGeneral.titulo || 'Formulario sin título',
        descripcion: formularioGeneral.descripcion || ''
      };
    }
    return { titulo: 'Formulario sin título', descripcion: '' };
  }, [formulariosGenerales]);

  const listaFiltrada = useMemo(() => {
    const filtrados = importados.filter((f) => f.visible === true);

    const filtros = {
      Abiertos: (f) => !fechaVencida(f.fecha_fin) && f.estado !== 'finalizado',
      Completados: (f) => f.estado === 'finalizado',
      Expirados: (f) => f.estado !== 'finalizado' && fechaVencida(f.fecha_fin)
    };

    let resultado = filtros[filtro] ? filtrados.filter(filtros[filtro]) : filtrados;

    return resultado.sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion));
  }, [importados, filtro, fechaVencida]);

  const continuarFormulario = useCallback(async (formulario) => {
    if (formularioGeneralInactivo(formulario)) {
      mostrarSnackbar('Este formulario está temporalmente inactivo', 'warning');
      return;
    }

    if (filtro === 'Abiertos' && fechaInicioFutura(formulario.fecha_inicio)) {
      mostrarSnackbar('Este formulario aún no está disponible', 'warning');
      return;
    }

    if (!formulario.id_formulario_general) {
      mostrarSnackbar('El formulario no tiene código general', 'error');
      return;
    }

    setLoadingId(formulario.id_formulario);
    try {
      const res = await fetch(`${API_URL}/api/formularios/continuar/${formulario.id_formulario_general}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al continuar');

      const rutaInicial = `/form/${tipoPersona}/1`;
      await fetch(`${API_URL}/api/formularios/${formulario.id_formulario}/ultima-ruta`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ultima_ruta: rutaInicial })
      });

      navigate(`${rutaInicial}?id_formulario=${formulario.id_formulario}`);
    } catch (error) {
      mostrarSnackbar(error.message, 'error');
    } finally {
      setLoadingId(null);
    }
  }, [formularioGeneralInactivo, fechaInicioFutura, filtro, mostrarSnackbar, tipoPersona, navigate]);

  const obtenerIcono = useCallback((estadoSection, formulario) => {
    if (estadoSection === 'Abiertos' && esReactivado(formulario)) {
      return <ReactivatedIcon />;
    }
    const iconos = {
      Abiertos: FileIcon,
      Completados: ViewIcon,
      Expirados: ExpiredIcon
    };
    const IconoComponente = iconos[estadoSection];
    return IconoComponente ? <IconoComponente /> : <FileIcon />;
  }, [esReactivado]);

  const obtenerEstadoFormulario = useCallback((formulario) => {
    if (formularioGeneralInactivo(formulario)) {
      return { color: 'default', texto: 'Inactivo' };
    }

    if (filtro === 'Abiertos' && esReactivado(formulario)) {
      return { color: 'warning', texto: 'Reactivado' };
    }

    if (filtro === 'Expirados') {
      return { color: 'error', texto: 'Expirado' };
    }

    return formulario.estado === 'finalizado'
      ? { color: 'success', texto: 'Completado' }
      : { color: 'primary', texto: 'Activo' };
  }, [formularioGeneralInactivo, filtro, esReactivado]);

  useEffect(() => {
    obtenerUsuario();
  }, [obtenerUsuario]);

  useEffect(() => {
    obtenerTodosLosDatos();

    intervalRef.current = setInterval(obtenerTodosLosDatos, 10000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [obtenerTodosLosDatos]);

  const renderFormulario = (formulario) => {
    const datosFormularioGeneral = obtenerDatosFormularioGeneral(formulario);
    const vence = fechaVencida(formulario.fecha_fin);
    const inicioFuturo = filtro === 'Abiertos' && fechaInicioFutura(formulario.fecha_inicio);
    const estaInactivo = formularioGeneralInactivo(formulario);
    const esCompletado = formulario.estado === 'finalizado';
    const reactivado = filtro === 'Abiertos' && esReactivado(formulario);
    const disabled = (filtro === 'Expirados') || estaInactivo || inicioFuturo;
    const estado = obtenerEstadoFormulario(formulario);

    let tooltipTitle = '';
    if (disabled) {
      if (estaInactivo) tooltipTitle = 'Formulario temporalmente inactivo';
      else if (inicioFuturo) tooltipTitle = 'El formulario aún no está disponible';
      else if (vence && filtro === 'Expirados') tooltipTitle = 'Formulario expirado';
    }

    let botonTexto = 'Continuar';
    if (estaInactivo) botonTexto = 'Inactivo';
    else if (inicioFuturo) botonTexto = 'No disponible';
    else if (formulario.estado === 'finalizado') botonTexto = 'Revisar';
    else if (reactivado) botonTexto = 'Continuar';

    return (
      <Fade in key={formulario.id_formulario}>
        <Card sx={{
          borderRadius: 3,
          boxShadow: 1,
          opacity: estaInactivo ? 0.7 : 1,
          border: estaInactivo ? '1px solid #e0e0e0' : 'none'
        }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                <Avatar sx={{
                  bgcolor: estaInactivo ? '#9e9e9e' : (reactivado ? '#ed6c02' : '#004892'),
                  flexShrink: 0
                }}>
                  {estaInactivo ? <InactiveIcon /> : obtenerIcono(filtro, formulario)}
                </Avatar>
                <Box minWidth={0}>
                  <Typography
                    variant="h6"
                    noWrap
                    title={datosFormularioGeneral.titulo}
                    sx={{
                      textTransform: 'uppercase',
                      fontSize: '1rem',
                      color: estaInactivo ? 'text.disabled' : 'text.primary'
                    }}
                  >
                    {datosFormularioGeneral.titulo}
                  </Typography>
                  {datosFormularioGeneral.descripcion && (
                    <Typography
                      variant="body2"
                      color={estaInactivo ? 'text.disabled' : 'text.secondary'}
                      noWrap
                      title={datosFormularioGeneral.descripcion}
                    >
                      {datosFormularioGeneral.descripcion}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Chip
                label={estado.texto}
                color={estado.color}
                size="small"
                variant={estaInactivo ? "outlined" : "filled"}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap={0.5} mb={1}>

              {filtro === 'Abiertos' && (
                <Typography variant="body2" color={estaInactivo ? 'text.disabled' : 'text.secondary'}>
                  Inicia: {formatFechaDisplay(formulario.fecha_inicio)}
                </Typography>
              )}

              <Typography variant="body2" color={estaInactivo ? 'text.disabled' : 'text.secondary'}>
                Expira: {formatFechaDisplay(formulario.fecha_fin)}
              </Typography>

              {esCompletado && (
                <Typography variant="body2" color={estaInactivo ? 'text.disabled' : 'text.secondary'}>
                  Finalizado: {formatFechaHoraDisplay(formulario.fecha_finalizacion)}
                </Typography>
              )}

            </Box>

            <Tooltip title={tooltipTitle}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={() => continuarFormulario(formulario)}
                  disabled={disabled}
                  startIcon={loadingId === formulario.id_formulario ? <CircularProgress size={16} /> : null}
                >
                  {botonTexto}
                </Button>
              </span>
            </Tooltip>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <Box sx={{ backgroundColor: '#f6f8fb', minHeight: '100vh' }}>
      <Box sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid #e5e8ec',
        px: { xs: 2, md: 4 },
        py: 2.5,
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
          <Box>
            <Typography variant="h5" fontWeight={700} mb={1}>
              Mis formularios
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              En esta sección se presentan todos los formularios que le han sido asignados.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {['Abiertos', 'Completados', 'Expirados'].map((tipo) => (
                <Chip
                  key={tipo}
                  label={tipo}
                  onClick={() => setFiltro(tipo)}
                  color={filtro === tipo ? 'primary' : 'default'}
                  variant={filtro === tipo ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>

      <Box px={{ xs: 2, md: 4 }} mt={3} pb={4} sx={{ maxHeight: 600, overflow: 'auto' }}>
        {listaFiltrada.length > 0 ? (
          <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2}>
            {listaFiltrada.map(renderFormulario)}
          </Box>
        ) : (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              No se encontraron formularios {filtro.toLowerCase()}.
            </Typography>
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}